/**
 * googleEditorCsv.ts — builds the file Google Ads Editor imports (Account → Import → From file).
 *
 * Google Ads has no API here, so approved ad groups go in through this file. Rules it follows, from Google's own
 * "Import a CSV file in Google Ads Editor" help pages: English headers, one thing per row (ad group, keyword, ad, callout,
 * campaign negative), "Campaign" names an existing campaign, and everything new arrives Paused. Sitelinks (they need a page
 * address each) and structured snippets (they need a header) are not written; they are listed for adding by hand.
 *
 * Column names and match-type words are the documented ones, but Editor is the judge: the first import is a test, and the
 * person importing can re-map any column in Editor's import screen.
 */
import { AD_PRODUCTS, RSA_LIMITS, type AdProductId } from "./adsProducts.ts";
import type { GooglePayload } from "./adsDrafts.ts";
import { adCode, buildUtmUrl, landingPageProblem } from "./adsLinks.ts";

export interface EditorItem {
  /** The draft's id. */
  id: string;
  payload: GooglePayload;
  /** The draft still has a problem that must be fixed (hasFixes). Such a draft is left out. */
  hasFixes: boolean;
}

export interface EditorSkip {
  id: string;
  adGroup: string;
  reason: string;
}

export interface EditorExport {
  csv: string;
  fileName: string;
  campaign: string;
  /** Draft ids written to the file (to be marked as exported). */
  exportedIds: string[];
  skipped: EditorSkip[];
  counts: { adGroups: number; ads: number; keywords: number; negatives: number; callouts: number };
  /** Approved assets the file does not carry, for adding by hand. */
  byHand: string[];
}

const HEADLINES = Array.from({ length: RSA_LIMITS.maxHeadlines }, (_, i) => i + 1);
const DESCRIPTIONS = Array.from({ length: RSA_LIMITS.maxDescriptions }, (_, i) => i + 1);

export const EDITOR_COLUMNS = [
  "Campaign",
  "Ad Group",
  "Ad Group Status",
  "Keyword",
  "Criterion Type",
  "Ad Type",
  "Final URL",
  ...HEADLINES.flatMap((n) => [`Headline ${n}`, `Headline ${n} position`]),
  ...DESCRIPTIONS.map((n) => `Description ${n}`),
  "Path 1",
  "Path 2",
  "Callout text",
  "Status",
] as const;

type Row = Partial<Record<(typeof EDITOR_COLUMNS)[number], string>>;

function cell(v: string): string {
  // A leading = + - @ can be run as a formula when the file is opened in a spreadsheet; keyword and negative signs are not
  // written this way, so a leading sign is always neutralised.
  const safe = /^[=+\-@]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

const matchWord = (m: string): "Exact" | "Phrase" => (m.trim().toLowerCase() === "exact" ? "Exact" : "Phrase");

export function buildGoogleEditorCsv(items: readonly EditorItem[], product: AdProductId, today: string): EditorExport {
  const campaign = AD_PRODUCTS[product].googleCampaign;
  const skipped: EditorSkip[] = [];
  const ok: Array<EditorItem & { url: string }> = [];
  for (const it of items) {
    const p = it.payload;
    const skip = (reason: string) => skipped.push({ id: it.id, adGroup: p.adGroup, reason });
    if (it.hasFixes) skip("It still has a problem to fix.");
    else if (!p.adGroup.trim()) skip("It has no ad group name.");
    else if (!p.finalUrl.trim()) skip("It has no landing page. Add one on the ad.");
    else {
      const bad = landingPageProblem(p.finalUrl, product);
      if (bad) skip(bad);
      else {
        const r = buildUtmUrl(p.finalUrl, { product, channel: "google_search", code: adCode(product, "google_search", it.id), keyword: p.keyword });
        if (r.ok) ok.push({ ...it, url: r.url });
        else skip(r.error);
      }
    }
  }

  const rows: Row[] = [];
  const seenGroups = new Set<string>();
  const seenKeywords = new Set<string>();
  const seenNegatives = new Set<string>();
  const seenCallouts = new Set<string>();
  const counts = { adGroups: 0, ads: 0, keywords: 0, negatives: 0, callouts: 0 };
  const byHand: string[] = [];

  for (const it of ok) {
    const p = it.payload;
    const group = p.adGroup.trim();
    const gKey = group.toLowerCase();
    if (!seenGroups.has(gKey)) {
      seenGroups.add(gKey);
      counts.adGroups++;
      rows.push({ Campaign: campaign, "Ad Group": group, "Ad Group Status": "Paused" });
    }
    for (const k of p.keywords) {
      const key = `${gKey}|${k.term.trim().toLowerCase()}|${matchWord(k.matchType)}`;
      if (!k.term.trim() || seenKeywords.has(key)) continue;
      seenKeywords.add(key);
      counts.keywords++;
      rows.push({ Campaign: campaign, "Ad Group": group, Keyword: k.term.trim(), "Criterion Type": matchWord(k.matchType), Status: "Enabled" });
    }
    const ad: Row = { Campaign: campaign, "Ad Group": group, "Ad Type": "Responsive search ad", "Final URL": it.url, "Path 1": p.path1, "Path 2": p.path2, Status: "Paused" };
    p.headlines.slice(0, RSA_LIMITS.maxHeadlines).forEach((h, i) => {
      ad[`Headline ${i + 1}` as keyof Row] = h.trim();
      // The first three headlines are the keyword variants and stay in position 1 (the rule checkRsa enforces).
      if (p.keyword.trim() && i < 3) ad[`Headline ${i + 1} position` as keyof Row] = "1";
    });
    p.descriptions.slice(0, RSA_LIMITS.maxDescriptions).forEach((d, i) => (ad[`Description ${i + 1}` as keyof Row] = d.trim()));
    rows.push(ad);
    counts.ads++;
    for (const c of p.callouts) {
      const k = c.trim().toLowerCase();
      if (!k || seenCallouts.has(k)) continue;
      seenCallouts.add(k);
      counts.callouts++;
      rows.push({ Campaign: campaign, "Callout text": c.trim() });
    }
    for (const n of p.negatives) {
      const k = n.term.trim().toLowerCase();
      if (!k || seenNegatives.has(k)) continue;
      seenNegatives.add(k);
      counts.negatives++;
      rows.push({ Campaign: campaign, Keyword: n.term.trim(), "Criterion Type": "Campaign Negative Phrase" });
    }
    for (const s of p.sitelinks) byHand.push(`Sitelink "${s.text}" (${group}): needs a page address`);
    for (const s of p.snippets) byHand.push(`Structured snippet "${s}" (${group}): needs a header`);
  }

  const lines = [EDITOR_COLUMNS.map(cell).join(",")];
  for (const r of rows) lines.push(EDITOR_COLUMNS.map((c) => cell(r[c] ?? "")).join(","));
  return {
    csv: "﻿" + lines.join("\r\n") + "\r\n",
    fileName: `google-ads-editor-${product.replace("_", "-")}-${today}.csv`,
    campaign,
    exportedIds: ok.map((o) => o.id),
    skipped,
    counts,
    byHand,
  };
}

/** The steps shown with the download, in plain words. */
export function editorImportSteps(campaign: string): string[] {
  return [
    `Open Google Ads Editor and download the account, so the campaign "${campaign}" is there.`,
    "Choose Account → Import → From file, and pick the downloaded file.",
    "Check that each column header is matched (Editor shows them in a list); fix any it flags.",
    "Select Import, then Review imported changes. Everything new is Paused.",
    "Read the errors and warnings Editor shows, accept the changes, then Post.",
  ];
}
